import { useEffect, useState } from 'react';
import Modal from '@tpct/mission-core/components/Modal';
import { toFileStoreError, type FileStoreApi } from '@tpct/mission-core/fileStore/fileStoreApi';
import type { FileUsage, StoredFile } from '@tpct/mission-core/fileStore/fileStoreTypes';
import { __ } from '@/hub-shared/utils/i18n';

type CompanyFileDeleteDialogProps = {
  api: FileStoreApi;
  ownerId: number;
  file: StoredFile;
  onDeleted: () => void;
  onClose: () => void;
};

type UsagesState =
  | { status: 'loading' }
  | { status: 'ready'; usages: FileUsage[] }
  | { status: 'error'; error: string };

const CLOSED_MISSION = 'file_in_closed_mission';

/** A refused delete: a closed mission is named, anything else shows the server's message. */
const deleteErrorMessage = (error: unknown): string => {
  const storeError = toFileStoreError(error);
  const missionLabel = storeError.data?.mission_label;
  if (storeError.code === CLOSED_MISSION && typeof missionLabel === 'string' && missionLabel !== '') {
    return `${__('Suppression impossible : ce fichier est joint à la mission close')} « ${missionLabel} ».`;
  }
  return storeError.message;
};

/**
 * Delete confirmation: lists where the file is attached (each attachment goes with it), and for a
 * DSN warns that the annual rollup is recomputed without it at the next DSN import (ledger 45).
 * A refusal keeps the dialog open with the reason; the row stays in the list.
 */
const CompanyFileDeleteDialog = ({ api, ownerId, file, onDeleted, onClose }: CompanyFileDeleteDialogProps) => {
  const [usages, setUsages] = useState<UsagesState>({ status: 'loading' });
  const [phase, setPhase] = useState<'idle' | 'deleting'>('idle');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    api.usages(ownerId, file.id).then(
      (response) => {
        if (!cancelled) {
          setUsages({ status: 'ready', usages: response.usages });
        }
      },
      (usagesError: unknown) => {
        if (!cancelled) {
          setUsages({ status: 'error', error: toFileStoreError(usagesError).message });
        }
      },
    );
    return () => {
      cancelled = true;
    };
  }, [api, ownerId, file.id]);

  const handleDelete = async () => {
    setPhase('deleting');
    setError(null);
    try {
      await api.remove(ownerId, file.id);
      onDeleted();
    } catch (deleteError) {
      setError(deleteErrorMessage(deleteError));
      setPhase('idle');
    }
  };

  const renderUsages = () => {
    if (usages.status === 'loading') {
      return <p role="status">{__('Recherche des utilisations…')}</p>;
    }
    if (usages.status === 'error') {
      return <p className="notice notice-warning">{usages.error}</p>;
    }
    if (usages.usages.length === 0) {
      return <p>{__('Ce fichier n’est joint à aucune mission.')}</p>;
    }
    return (
      <>
        <p>{__('Il sera retiré de :')}</p>
        <ul className="company-files-tab__usages">
          {usages.usages.map((usage) => <li key={usage.link_id}>{usage.label}</li>)}
        </ul>
      </>
    );
  };

  return (
    <Modal
      isOpen
      onClose={onClose}
      title={`${__('Supprimer')} « ${file.name} » ?`}
      size="small"
      closeOnOverlayClick={phase === 'idle'}
    >
      {renderUsages()}
      {file.type_key === 'dsn' && (
        <p className="notice notice-info">
          {__('Les totaux annuels DSN seront recalculés sans ce fichier au prochain import DSN.')}
        </p>
      )}
      {error !== null && <p className="notice notice-error" role="alert">{error}</p>}
      <div className="form-actions">
        <button type="button" className="btn-secondary" disabled={phase === 'deleting'} onClick={onClose}>
          {__('Annuler')}
        </button>
        <button
          type="button"
          className="btn-danger"
          disabled={phase === 'deleting' || usages.status === 'loading'}
          onClick={() => {
            void handleDelete();
          }}
        >
          {phase === 'deleting' ? __('Suppression…') : __('Supprimer')}
        </button>
      </div>
    </Modal>
  );
};

export default CompanyFileDeleteDialog;
